// src/componentes/GestionPermisosTecnicos.js
import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config/api';

const GestionPermisosTecnicos = () => {
  const API = useMemo(() => API_BASE, []);

  const [tecnicos, setTecnicos] = useState([]);
  const [colegios, setColegios] = useState([]);
  const [salas, setSalas] = useState([]);

  const [tecnicoId, setTecnicoId] = useState('');
  const [colegioId, setColegioId] = useState('');
  const [salaId, setSalaId] = useState('');

  const [permisos, setPermisos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // Cargar técnicos y colegios
  useEffect(() => {
    const cargarInicial = async () => {
      try {
        const [resAdmins, resColegios] = await Promise.all([
          fetch(`${API}/api/administradores`),
          fetch(`${API}/api/colegios`)
        ]);

        const admins = resAdmins.ok ? await resAdmins.json() : [];
        const col = resColegios.ok ? await resColegios.json() : [];

        // solo rol tecnico
        const soloTecnicos = (admins || []).filter(
          (a) => String(a.rol).toLowerCase() === 'tecnico'
        );

        setTecnicos(Array.isArray(soloTecnicos) ? soloTecnicos : []);
        setColegios(Array.isArray(col) ? col : []);
      } catch (e) {
        console.error('Error cargando datos iniciales:', e);
        alert('Error al cargar técnicos/colegios');
      }
    };

    cargarInicial();
  }, [API]);

  // Cargar permisos del técnico seleccionado
  useEffect(() => {
    const cargarPermisos = async () => {
      if (!tecnicoId) {
        setPermisos([]);
        return;
      }
      try {
        setCargando(true);
        const res = await fetch(`${API}/api/permisos/tecnico/${tecnicoId}`);
        const data = res.ok ? await res.json() : [];
        setPermisos(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Error cargando permisos:', e);
        alert('Error al cargar permisos del técnico');
      } finally {
        setCargando(false);
      }
    };

    cargarPermisos();
  }, [API, tecnicoId]);

  // Cargar salas cuando cambia colegio
  useEffect(() => {
    const cargarSalas = async () => {
      if (!colegioId) {
        setSalas([]);
        setSalaId('');
        return;
      }
      try {
        const res = await fetch(`${API}/api/colegios/${colegioId}/salas`);
        const data = res.ok ? await res.json() : [];
        setSalas(Array.isArray(data) ? data : []);
        setSalaId('');
      } catch (e) {
        console.error('Error cargando salas:', e);
        alert('Error al cargar salas del colegio');
      }
    };

    cargarSalas();
  }, [API, colegioId]);

  const tecnicoSeleccionado = useMemo(() => {
    return tecnicos.find((t) => String(t.id) === String(tecnicoId));
  }, [tecnicos, tecnicoId]);

  const guardarPermiso = async (tecnico_id, sala_id, activo) => {
    try {
      setGuardando(true);
      const res = await fetch(`${API}/api/permisos/tecnico-sala`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tecnico_id,
          sala_id,
          activo: activo ? 1 : 0
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.mensaje || 'Error al guardar permiso');
        return;
      }

      // refrescar permisos
      const resPerm = await fetch(`${API}/api/permisos/tecnico/${tecnico_id}`);
      const perm = resPerm.ok ? await resPerm.json() : [];
      setPermisos(Array.isArray(perm) ? perm : []);
    } catch (e) {
      console.error('Error guardando permiso:', e);
      alert('Error al guardar permiso');
    } finally {
      setGuardando(false);
    }
  };

  const otorgarPermiso = async () => {
    if (!tecnicoId) return alert('Seleccioná un técnico');
    if (!colegioId) return alert('Seleccioná un colegio');
    if (!salaId) return alert('Seleccioná una sala');

    await guardarPermiso(tecnicoId, salaId, true);

    // dejar listo para seguir cargando
    setSalaId('');
  };

  const togglePermiso = async (perm) => {
    if (!perm?.tecnico_id || !perm?.sala_id) return;
    const nuevo = Number(perm.activo) === 1 ? 0 : 1;
    await guardarPermiso(perm.tecnico_id, perm.sala_id, nuevo === 1);
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-blue-700">Permisos de Técnicos (por Sala)</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel otorgar permiso */}
        <div className="bg-white shadow rounded-lg p-5 space-y-4">
          <h3 className="text-lg font-semibold">Otorgar / Activar permiso</h3>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Técnico</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={tecnicoId}
              onChange={(e) => {
                setTecnicoId(e.target.value);
                setColegioId('');
                setSalaId('');
                setSalas([]);
              }}
            >
              <option value="">-- Seleccionar técnico --</option>
              {tecnicos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.usuario} (ID: {t.id})
                </option>
              ))}
            </select>
            {tecnicoId && (
              <p className="text-sm text-gray-500">
                Técnico seleccionado:{' '}
                <span className="font-semibold">{tecnicoSeleccionado?.usuario}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Colegio</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={colegioId}
              onChange={(e) => setColegioId(e.target.value)}
              disabled={!tecnicoId}
            >
              <option value="">-- Seleccionar colegio --</option>
              {colegios.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
            {!tecnicoId && (
              <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                Seleccioná primero un técnico.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Sala</label>
            <select
              className="w-full border px-3 py-2 rounded"
              value={salaId}
              onChange={(e) => setSalaId(e.target.value)}
              disabled={!tecnicoId || !colegioId}
            >
              <option value="">-- Seleccionar sala --</option>
              {salas.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre} ({s.tipo})
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={otorgarPermiso}
            disabled={guardando || !tecnicoId || !colegioId || !salaId}
            className={`w-full px-4 py-2 rounded text-white transition ${
              guardando || !tecnicoId || !colegioId || !salaId
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {guardando ? 'Guardando...' : 'Otorgar permiso a esta sala'}
          </button>

          <p className="text-sm text-gray-500">
            Nota: si ya existe el permiso, se actualiza y se renueva la fecha de autorización.
          </p>
        </div>

        {/* Panel listado permisos */}
        <div className="bg-white shadow rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Permisos actuales</h3>
            {cargando && <span className="text-sm text-gray-500">Cargando...</span>}
          </div>

          {!tecnicoId ? (
            <div className="text-gray-600 bg-gray-50 border rounded p-4">
              Seleccioná un técnico para ver y gestionar sus permisos.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded overflow-hidden">
                <thead className="bg-blue-100 text-left">
                  <tr>
                    <th className="px-4 py-2">Colegio</th>
                    <th className="px-4 py-2">Sala</th>
                    <th className="px-4 py-2">Tipo</th>
                    <th className="px-4 py-2">Estado</th>
                    <th className="px-4 py-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {(permisos || []).map((p) => (
                    <tr key={p.id} className="border-t">
                      <td className="px-4 py-2">{p.colegio}</td>
                      <td className="px-4 py-2">{p.sala}</td>
                      <td className="px-4 py-2">{p.tipo_sala}</td>
                      <td className="px-4 py-2">
                        {Number(p.activo) === 1 ? (
                          <span className="px-2 py-1 rounded text-sm bg-green-100 text-green-700">Activo</span>
                        ) : (
                          <span className="px-2 py-1 rounded text-sm bg-red-100 text-red-700">Inactivo</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => togglePermiso(p)}
                          disabled={guardando}
                          className={`px-3 py-1 rounded text-white transition ${
                            Number(p.activo) === 1
                              ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-green-600 hover:bg-green-700'
                          } ${guardando ? 'opacity-60 cursor-not-allowed' : ''}`}
                          title="Activar/Desactivar"
                        >
                          {Number(p.activo) === 1 ? 'Desactivar' : 'Activar'}
                        </button>
                      </td>
                    </tr>
                  ))}

                  {(permisos || []).length === 0 && !cargando && (
                    <tr>
                      <td colSpan="5" className="px-4 py-4 text-center text-gray-500">
                        Este técnico aún no tiene permisos asignados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 text-sm text-gray-500">
            * Los permisos son por sala (como acordamos), para evitar cambios no autorizados en otros lugares.
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestionPermisosTecnicos;